/**
 * Leaderboards - its own page, apart from the badge collection.
 *
 * Three boards: community points, hours played and the top crews. Each shows
 * the top ten; if you are further down, your own place is pinned under the
 * list so you always know where you stand.
 *
 * Hours played is time the server counted itself while a game was running
 * and the app was connected - in short slices, capped per day - so it cannot
 * be pumped up by a modified client.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/icons/Icon';
import { api } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { Avatar } from '@/components/Avatar';
import { UserName, openUserCard } from '@/components/user';
import { CrewTag, openCrew } from '@/components/Crew';
import { absolute } from '@/components/user';
import type { CrewChip } from '@/components/CrewTag';
import { Empty } from '@/components/ui';
import { Medal } from './BadgesView';

type Period = 'week' | 'month' | 'all';
type Board = 'points' | 'hours' | 'crews';

type PointsRow = { rank: number; user: PublicUser; hosted: number; points: number };
type HoursRow = { rank: number; user: PublicUser; seconds: number; topGame: string | null };
type CrewRow = { rank: number; crew: CrewChip & { level: number }; members: number; xp: number };

const PANEL = 'pugo-panel rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]';
const PERIODS: [Period, string][] = [['week', 'This week'], ['month', 'This month'], ['all', 'All time']];
const BOARDS: [Board, string, string][] = [
  ['points', 'Players', 'trophy'],
  ['hours', 'Hours played', 'clock'],
  ['crews', 'Top crews', 'friends']
];

/** 3h 25m, 45m, or 0m. */
export function formatPlayTime(seconds: number): string {
  const mins = Math.floor((seconds || 0) / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return m + 'm';
  return h.toLocaleString() + 'h' + (m ? ' ' + m + 'm' : '');
}

export function LeaderboardView() {
  const [board, setBoard] = useState<Board>('points');
  const [period, setPeriod] = useState<Period>('week');

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 px-4 py-5 sm:px-6 xl:px-8">
        <header className="flex flex-wrap items-end gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="m-0 text-[32px] leading-tight font-extrabold tracking-[-.03em] text-ink">Leaderboards</h1>
            <p className="mt-1 mb-0 text-[15px] text-mute">The top ten in each board - and where you stand.</p>
          </div>
          <button
            type="button"
            onClick={() => go('badges')}
            className="flex h-10 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-3.5 text-[14px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line)] outline-none transition-colors hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            <Icon name="badges" size={16} />
            Badge collection
          </button>
        </header>

        <div className={cn(PANEL, 'p-4 sm:p-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div role="tablist" aria-label="Board" className="flex gap-1 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-1">
              {BOARDS.map(([id, label, icon]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={board === id}
                  onClick={() => setBoard(id)}
                  className={cn(
                    'flex h-10 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] px-4 text-[14.5px] font-semibold outline-none transition-[background-color,color,box-shadow] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    board === id ? 'bg-accent text-accent-fg shadow-[0_6px_16px_-10px_var(--accent)]' : 'text-dim hover:text-ink'
                  )}
                >
                  <Icon name={icon} size={16} />
                  {label}
                </button>
              ))}
            </div>
            <span className="flex-1" />
            {board !== 'crews' ? <PeriodPicker period={period} onChange={setPeriod} /> : null}
          </div>

          <div key={board} className="mt-5 animate-[fade-in_240ms_ease-out] motion-reduce:animate-none">
            {board === 'points' ? <PointsBoard period={period} /> : board === 'hours' ? <HoursBoard period={period} /> : <CrewsBoard />}
          </div>
        </div>
      </div>
    </div>
  );
}

function PeriodPicker({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  const track = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const el = track.current?.querySelector<HTMLElement>('[data-period="' + period + '"]');
    if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
  }, [period]);
  return (
    <div ref={track} role="radiogroup" aria-label="Period" className="relative flex rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-1">
      {pill ? (
        <span aria-hidden className="absolute top-1 bottom-1 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-4 transition-[left,width] duration-[300ms] ease-[var(--ease-out)] motion-reduce:transition-none" style={{ left: pill.left, width: pill.width }} />
      ) : null}
      {PERIODS.map(([id, label]) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={period === id}
          data-period={id}
          onClick={() => onChange(id)}
          className={cn('relative z-[1] h-9 rounded-[calc(12px*var(--radius-mul,1))] px-3.5 text-[13.5px] font-semibold outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]', period === id ? 'text-ink' : 'text-mute hover:text-dim')}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- shared */

function Table({ head, children, loading, error, empty }: {
  head: [string, string];
  children: ReactNode;
  loading: boolean;
  error: Error | null;
  empty: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-[calc(18px*var(--radius-mul,1))] shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="grid grid-cols-[72px_minmax(0,1fr)_150px] items-center gap-3 border-b border-line-soft px-5 py-3 text-[13px] font-semibold text-mute">
        <span>Rank</span>
        <span>{head[0]}</span>
        <span className="text-right">{head[1]}</span>
      </div>
      {loading ? (
        <div className="flex flex-col">{[0, 1, 2, 3].map((i) => <div key={i} className="m-3 h-14 animate-pulse rounded-[12px] bg-surface-2" />)}</div>
      ) : error ? (
        <div className="p-6"><Empty title="Could not load this board" note={error.message} /></div>
      ) : empty ? (
        <div className="p-10 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-accent-soft text-accent"><Icon name="trophy" size={26} /></span>
          <p className="mt-3 mb-0 text-[16px] font-semibold text-ink">Nobody on this board yet</p>
          <p className="mt-1 mb-0 text-[14px] text-mute">Play, host and hang out - the first places are open.</p>
        </div>
      ) : (
        <ul className="m-0 list-none p-0">{children}</ul>
      )}
    </div>
  );
}

function RankCell({ rank }: { rank: number }) {
  return (
    <span className="grid place-items-start pl-1">
      {rank <= 3 ? <Medal rank={rank} /> : <span className="w-11 text-center text-[19px] font-bold text-ink tabular-nums">{rank}</span>}
    </span>
  );
}

function Line({ rank, mine, index, children, value }: { rank: number; mine: boolean; index: number; children: ReactNode; value: ReactNode }) {
  return (
    <li
      style={{ animationDelay: Math.min(index, 10) * 30 + 'ms' }}
      className={cn(
        'grid grid-cols-[72px_minmax(0,1fr)_150px] items-center gap-3 border-b border-line-soft px-5 py-2.5 last:border-b-0',
        'animate-[fade-in_280ms_ease-out_backwards] motion-reduce:animate-none',
        mine ? 'bg-accent-soft' : 'hover:bg-surface-2'
      )}
    >
      <RankCell rank={rank} />
      <span className="flex min-w-0 items-center gap-3.5">{children}</span>
      <span className="text-right text-[18px] font-bold text-ink tabular-nums">{value}</span>
    </li>
  );
}

function PlayerCell({ user, line }: { user: PublicUser; line: string }) {
  return (
    <>
      <button
        type="button"
        onClick={(e) => openUserCard(user, e.currentTarget)}
        aria-label={'Show ' + user.displayName + "'s profile card"}
        className="flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <Avatar user={user} size={44} />
      </button>
      <span className="min-w-0">
        <UserName user={user} className="block truncate text-[15.5px] font-bold" />
        <span className="block truncate text-[13px] text-mute">{line}</span>
      </span>
    </>
  );
}

/** Your place, when it is not already in the top ten. */
function YourPlace({ rank, total, label, value, children }: { rank: number; total: number; label: string; value: string; children?: ReactNode }) {
  return (
    <div className="mt-3 flex items-center gap-3.5 rounded-[calc(16px*var(--radius-mul,1))] bg-accent-soft px-5 py-3 shadow-[inset_0_0_0_1px_var(--accent-line)]">
      <span className="grid h-11 w-11 flex-none place-items-center rounded-full bg-accent text-[15px] font-extrabold text-accent-fg tabular-nums">#{rank}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-bold text-ink">{label}</span>
        <span className="block text-[13px] text-mute">Place {rank.toLocaleString()} of {total.toLocaleString()}{children}</span>
      </span>
      <span className="text-[18px] font-bold text-ink tabular-nums">{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------- boards */

function PointsBoard({ period }: { period: Period }) {
  const me = useSession((s) => s.me);
  const q = useQuery({
    queryKey: ['leaderboard', period],
    queryFn: () => api.get<{ players: PointsRow[]; total: number; me: PointsRow | null }>('/api/leaderboard?period=' + period),
    placeholderData: (prev) => prev
  });
  const rows = (q.data?.players ?? []).slice(0, 10);
  const mine = q.data?.me ?? null;
  const shown = !!mine && rows.some((r) => r.user.id === mine.user.id);
  return (
    <>
      <Table head={['Player', 'Points']} loading={q.isPending} error={q.error as Error | null} empty={!rows.length}>
        {rows.map((r, i) => (
          <Line key={r.user.id} rank={r.rank} mine={r.user.id === me?.id} index={i} value={r.points.toLocaleString()}>
            <PlayerCell user={r.user} line={r.hosted + (r.hosted === 1 ? ' party hosted' : ' parties hosted') + ' · Level ' + r.user.level} />
          </Line>
        ))}
      </Table>
      {mine && !shown && mine.points > 0 ? <YourPlace rank={mine.rank} total={q.data?.total ?? 0} label="You" value={mine.points.toLocaleString()} /> : null}
    </>
  );
}

function HoursBoard({ period }: { period: Period }) {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const q = useQuery({
    queryKey: ['leaderboard-hours', period],
    queryFn: () => api.get<{ players: HoursRow[]; total: number; me: HoursRow | null; dayCapHours: number }>('/api/leaderboard/hours?period=' + period),
    placeholderData: (prev) => prev
  });
  const rows = q.data?.players ?? [];
  const mine = q.data?.me ?? null;
  const shown = !!mine && rows.some((r) => r.user.id === mine.user.id);
  const gameName = (id: string | null) => (id ? catalog?.games.find((g) => g.id === id)?.name ?? id : null);
  return (
    <>
      <Table head={['Player', 'Time played']} loading={q.isPending} error={q.error as Error | null} empty={!rows.length}>
        {rows.map((r, i) => (
          <Line key={r.user.id} rank={r.rank} mine={r.user.id === me?.id} index={i} value={formatPlayTime(r.seconds)}>
            <PlayerCell user={r.user} line={gameName(r.topGame) ? 'Mostly ' + gameName(r.topGame) : 'Level ' + r.user.level} />
          </Line>
        ))}
      </Table>
      {mine && !shown && mine.seconds > 0 ? (
        <YourPlace rank={mine.rank} total={Math.max(q.data?.total ?? 0, mine.rank)} label="You" value={formatPlayTime(mine.seconds)} />
      ) : null}
      <p className="mt-4 mb-0 flex items-center gap-2 text-[13px] text-mute">
        <Icon name="shield" size={15} className="flex-none" />
        Counted by Pugo while a game is running and the app is open - at most {q.data?.dayCapHours ?? 16} hours a day, so the board stays honest.
      </p>
    </>
  );
}

function CrewsBoard() {
  const me = useSession((s) => s.me);
  const q = useQuery({
    queryKey: ['leaderboard-crews'],
    queryFn: () => api.get<{ crews: CrewRow[]; total: number; mine: CrewRow | null }>('/api/leaderboard/crews')
  });
  const rows = q.data?.crews ?? [];
  const mine = q.data?.mine ?? null;
  const shown = !!mine && rows.some((r) => r.crew.id === mine.crew.id);
  return (
    <>
      <Table head={['Crew', 'Crew level']} loading={q.isPending} error={q.error as Error | null} empty={!rows.length}>
        {rows.map((r, i) => (
          <Line key={r.crew.id} rank={r.rank} mine={r.crew.id === me?.crew?.id} index={i} value={'Lv ' + r.crew.level}>
            <button
              type="button"
              onClick={() => openCrew(r.crew.tag)}
              className="flex min-w-0 items-center gap-3.5 rounded-[12px] text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <span
                className="grid h-12 w-12 flex-none place-items-center overflow-hidden rounded-[13px] text-[13px] font-extrabold text-white"
                style={{
                  background: r.crew.icon ? 'var(--bg-2)' : (r.crew.design?.swatch ?? 'var(--bg-3)'),
                  color: r.crew.design?.ink ?? '#fff',
                  boxShadow: 'inset 0 0 0 1px var(--line-soft)'
                }}
              >
                {r.crew.icon
                  ? <img src={absolute(r.crew.icon)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  : r.crew.tag.slice(0, 4)}
              </span>
              <span className="min-w-0">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[15.5px] font-bold text-ink">{r.crew.name}</span>
                  <CrewTag crew={r.crew} size="md" />
                </span>
                <span className="block truncate text-[13px] text-mute">{r.members} {r.members === 1 ? 'member' : 'members'} · {r.xp.toLocaleString()} XP together</span>
              </span>
            </button>
          </Line>
        ))}
      </Table>
      {mine && !shown ? <YourPlace rank={mine.rank} total={q.data?.total ?? 0} label={mine.crew.name} value={'Lv ' + mine.crew.level} /> : null}
      {!me?.crew ? (
        <p className="mt-4 mb-0 text-[13.5px] text-mute">
          Not in a crew yet? <button type="button" onClick={() => go('crews?find=1')} className="font-semibold text-accent hover:underline">Find one</button>
        </p>
      ) : null}
    </>
  );
}
